import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import clsx from "clsx";
import * as api from "../../api/statoLavori";
import { getErrorMessage } from "../../api/client";
import { SERVIZI_PRECREDIT, SERVIZIO_PRECREDIT_LABELS, TipoServizioPrecredit } from "../../api/devices";
import { Button } from "../../components/ui/Button";
import { useRicercaUrl, useScriviParametri } from "../../hooks/useStatoUrl";
import { AvanzamentoPratica, BozzePratica, FasePratica, SegnalazioniPratica, ServizioPratica } from "./PezziPratica";
import { dataIt, nomeCassaforte, nomeSede } from "./presentazione";

// ============================================================================
// STATO LAVORI — elenco delle pratiche (issue #211, PR B3)
//
// Una riga per pratica, cioè per cliente e cassaforte. Tutto ciò che si vede
// (fase, avanzamento, prossimo passo, segnalazioni) lo calcola il backend; i
// filtri stanno nell'URL come nel resto dell'app (`hooks/useStatoUrl.ts`).
// Il filtro e la tessera «Bozze da confermare» contano le bozze dei passi 7 e 9 (PR C2).
// ============================================================================

const STATI: { valore: api.FiltroStato; etichetta: string }[] = [
  { valore: "in_corso", etichetta: "In corso" },
  { valore: "completate", etichetta: "Completate" },
  { valore: "chiuse", etichetta: "Chiuse" },
  { valore: "tutte", etichetta: "Tutte" },
];

const FILTRI: { valore: api.FiltroSegnalazione; etichetta: string }[] = [
  { valore: "bloccate", etichetta: "Bloccate" },
  { valore: "da_verificare", etichetta: "Da verificare" },
  { valore: "promemoria_ordini", etichetta: "Con promemoria ordini" },
  { valore: "da_confermare", etichetta: "Bozze da confermare" },
];

function Tessera({ etichetta, valore, colore }: { etichetta: string; valore: number | string; colore: string }) {
  return (
    <div className="rounded-card border border-line bg-white p-4 shadow-card">
      <p className="text-xs font-semibold uppercase tracking-wide text-ink/45">{etichetta}</p>
      <p className={clsx("mt-1 text-2xl font-bold", colore)}>{valore}</p>
    </div>
  );
}

/** Un bottone a pillola dei filtri. `aria-pressed` dice a chi usa uno screen reader quale è attivo. */
function Pillola({ attiva, onClick, children }: { attiva: boolean; onClick: () => void; children: string }) {
  return (
    <button
      type="button"
      aria-pressed={attiva}
      onClick={onClick}
      className={clsx(
        "h-8 rounded-full border px-3 text-[13px] font-semibold transition-colors",
        attiva ? "border-primary bg-primary text-white" : "border-line bg-white text-ink hover:bg-primary/5"
      )}
    >
      {children}
    </button>
  );
}

export default function StatoLavori() {
  const [searchParams] = useSearchParams();
  const scrivi = useScriviParametri();
  const { testo: ricerca, setTesto: setRicerca, valore: ricercaAttiva } = useRicercaUrl();

  const statoUrl = searchParams.get("stato");
  const stato: api.FiltroStato = STATI.some((s) => s.valore === statoUrl) ? (statoUrl as api.FiltroStato) : "in_corso";
  const filtroUrl = searchParams.get("filtro");
  const filtro = FILTRI.some((f) => f.valore === filtroUrl) ? (filtroUrl as api.FiltroSegnalazione) : null;
  const servizioUrl = searchParams.get("servizio");
  const servizio =
    servizioUrl === "da_verificare" || SERVIZI_PRECREDIT.includes(servizioUrl as TipoServizioPrecredit)
      ? (servizioUrl as TipoServizioPrecredit | "da_verificare")
      : null;

  const [dati, setDati] = useState<api.ElencoPratiche | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [tentativo, setTentativo] = useState(0);

  useEffect(() => {
    let annullato = false;
    setIsLoading(true);
    setErrore(null);
    api
      .listPratiche({ stato, filtro, servizio, q: ricercaAttiva })
      .then((d) => {
        if (!annullato) setDati(d);
      })
      .catch((err) => {
        if (!annullato) setErrore(getErrorMessage(err, "Impossibile caricare le pratiche."));
      })
      .finally(() => {
        if (!annullato) setIsLoading(false);
      });
    return () => {
      annullato = true;
    };
  }, [stato, filtro, servizio, ricercaAttiva, tentativo]);

  const filtroAttivo = stato !== "in_corso" || filtro !== null || servizio !== null || ricercaAttiva !== "";

  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-bold text-ink">Stato lavori</h1>
        <p className="mt-1 text-sm text-ink/60">
          Le pratiche, dalla richiesta dei documenti all&apos;attivazione. Una riga per pratica, cioè per cliente e
          cassaforte. Una pratica si avvia dalla scheda della sede.
        </p>
      </header>

      {/* Le tessere contano le pratiche in corso, qualunque sia il filtro scelto. */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Tessera etichetta="Pratiche in corso" valore={dati?.conteggi.in_corso ?? "—"} colore="text-ink" />
        <Tessera etichetta="Bloccate" valore={dati?.conteggi.bloccate ?? "—"} colore="text-[#b91c1c]" />
        <Tessera etichetta="Servizio da verificare" valore={dati?.conteggi.servizio_da_verificare ?? "—"} colore="text-[#b45309]" />
        <Tessera etichetta="Con promemoria ordini" valore={dati?.conteggi.con_promemoria_ordini ?? "—"} colore="text-[#b45309]" />
        <Tessera etichetta="Bozze da confermare" valore={dati?.conteggi.con_bozze ?? "—"} colore="text-[#b45309]" />
      </div>

      <section className="overflow-hidden rounded-card border border-line bg-white shadow-card">
        <div className="flex flex-col gap-3 p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Stato delle pratiche">
              {STATI.map((s) => (
                <Pillola key={s.valore} attiva={stato === s.valore} onClick={() => scrivi({ stato: s.valore === "in_corso" ? null : s.valore })}>
                  {s.etichetta}
                </Pillola>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink/40" aria-hidden="true" />
                <label htmlFor="cerca-pratiche" className="sr-only">
                  Cerca cliente o sede
                </label>
                <input
                  id="cerca-pratiche"
                  type="search"
                  value={ricerca}
                  onChange={(e) => setRicerca(e.target.value)}
                  placeholder="Cerca cliente o sede"
                  className="h-10 w-60 rounded-lg border border-line bg-white pl-9 pr-3 text-sm text-ink placeholder:text-ink/40"
                />
              </div>
              <label htmlFor="filtro-servizio" className="sr-only">
                Servizio
              </label>
              <select
                id="filtro-servizio"
                value={servizio ?? ""}
                onChange={(e) => scrivi({ servizio: e.target.value || null })}
                className="h-10 rounded-lg border border-line bg-white px-3 text-sm text-ink"
              >
                <option value="">Servizio: tutti</option>
                <option value="da_verificare">Servizio da verificare</option>
                {SERVIZI_PRECREDIT.map((s) => (
                  <option key={s} value={s}>
                    {SERVIZIO_PRECREDIT_LABELS[s]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex flex-wrap gap-2" role="group" aria-label="Segnalazioni">
            {FILTRI.map((f) => (
              <Pillola key={f.valore} attiva={filtro === f.valore} onClick={() => scrivi({ filtro: filtro === f.valore ? null : f.valore })}>
                {f.etichetta}
              </Pillola>
            ))}
          </div>
        </div>

        {errore ? (
          <div className="flex flex-col items-center gap-3 border-t border-line px-4 py-10 text-center">
            <p className="text-sm text-[#b91c1c]">{errore}</p>
            <Button variant="secondary" size="sm" onClick={() => setTentativo((t) => t + 1)}>
              Riprova
            </Button>
          </div>
        ) : isLoading && !dati ? (
          <p className="border-t border-line px-4 py-10 text-center text-sm text-ink/50">Caricamento…</p>
        ) : dati && dati.righe.length === 0 ? (
          <p className="border-t border-line px-4 py-10 text-center text-sm text-ink/50">
            {filtroAttivo
              ? "Nessuna pratica corrisponde ai filtri."
              : "Nessuna pratica in corso. Una pratica si avvia dalla scheda della sede, con «Avvia pratica»."}
          </p>
        ) : dati ? (
          <div className={clsx("overflow-x-auto", isLoading && "opacity-60")} aria-busy={isLoading}>
            <table className="w-full min-w-[1080px] border-collapse text-sm">
              <thead>
                <tr className="border-y border-line bg-[#F8F9F7] text-left text-xs font-semibold uppercase tracking-wide text-ink/60">
                  <th scope="col" className="px-4 py-3">Cliente e sede</th>
                  <th scope="col" className="px-4 py-3">Servizio</th>
                  <th scope="col" className="px-4 py-3">Fase attuale</th>
                  <th scope="col" className="px-4 py-3 whitespace-nowrap">Installare entro</th>
                  <th scope="col" className="px-4 py-3">Avanzamento</th>
                  <th scope="col" className="px-4 py-3">Prossimo passo</th>
                  <th scope="col" className="px-4 py-3">Segnalazioni</th>
                </tr>
              </thead>
              <tbody>
                {dati.righe.map((r) => (
                  <RigaElenco key={r.id} r={r} />
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <p className="border-t border-line px-4 py-3 text-xs text-ink/55">
          L&apos;avanzamento conta i passi completati sui passi necessari: quelli «non necessari» escono dal totale.
        </p>
      </section>
    </div>
  );
}

function RigaElenco({ r }: { r: api.RigaPratica }) {
  return (
    <tr className="border-b border-line align-top last:border-b-0 hover:bg-[#FBF7EC]">
      <td className="min-w-[200px] px-4 py-3.5">
        <Link to={`/stato-lavori/${r.id}`} className="font-semibold text-ink hover:underline">
          {r.cliente.nome}
        </Link>
        <p className="mt-0.5 text-xs text-ink/60">{nomeSede(r.sede)}</p>
        <p className="mt-0.5 text-xs text-ink/50">Cassaforte: {nomeCassaforte(r.cassaforte)}</p>
        <Link to={`/sedi/${r.sede.id}`} className="mt-1.5 inline-block text-xs font-semibold text-primary hover:underline">
          Scheda sede in Anagrafiche
        </Link>
      </td>
      <td className="whitespace-nowrap px-4 py-3.5">
        <ServizioPratica servizio={r.servizio} />
      </td>
      <td className="min-w-[190px] px-4 py-3.5">
        <FasePratica fase={r.fase} />
      </td>
      <td className="whitespace-nowrap px-4 py-3.5">{dataIt(r.installare_entro)}</td>
      <td className="min-w-[170px] px-4 py-3.5">
        <AvanzamentoPratica avanzamento={r.avanzamento} />
      </td>
      <td className="min-w-[200px] px-4 py-3.5">
        {r.prossimo_passo ? (
          <>
            <p>{r.prossimo_passo.azione}</p>
            <p className="mt-0.5 text-xs text-ink/60">{r.prossimo_passo.responsabile?.nome ?? "Nessun responsabile"}</p>
          </>
        ) : (
          <span className="text-ink/50">—</span>
        )}
      </td>
      <td className="min-w-[160px] px-4 py-3.5">
        <BozzePratica n={r.bozze_da_confermare} />
        <SegnalazioniPratica segnalazioni={r.segnalazioni} />
      </td>
    </tr>
  );
}
